import { useMemo } from 'react';
import { ConfigureIcon, Export, MultiSelect, PricingIcon, VenueIcon } from '@weareopenr/component-library';
import type { SelectedMultiOption } from '@weareopenr/component-library/build/components/MultiSelect/utilities';
import { EVENT_TYPES } from 'src/api/getEvents/types';
import { useVenues } from 'src/hooks/useVenues/useVenues';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import type { usePublishScheduleFilters } from 'src/hooks/usePublishScheduleFilters';
import { NEXT_WEEK_TITLE, THIS_WEEK_TITLE } from 'src/utils/schedules/groupPublishingByWeek';
import type { IScheduleWeek } from 'src/utils/schedules/groupPublishingByWeek';

const PUBLISH_TYPE_OPTIONS: SelectedMultiOption[] = [
  { id: EVENT_TYPES.PUBLISH_MENU, label: 'Menu publish', display_order: 0 },
  { id: EVENT_TYPES.PUBLISH_PRODUCT, label: 'Product publish', display_order: 1 },
  { id: EVENT_TYPES.PUBLISH_PRICING, label: 'Pricing publish', display_order: 2 },
  { id: EVENT_TYPES.PUBLISH_TIMETABLE, label: 'Timetable publish', display_order: 3 },
  { id: EVENT_TYPES.PUBLISH_MENU_SET, label: 'Menu set publish', display_order: 4 },
];

export type PublishScheduleFiltersProps = Pick<
  ReturnType<typeof usePublishScheduleFilters>,
  | 'eventTypes'
  | 'setEventTypes'
  | 'weekTitles'
  | 'setWeekTitles'
  | 'venueIds'
  | 'setVenueIds'
  | 'priceSchemeIds'
  | 'setPriceSchemeIds'
> & {
  weeks: IScheduleWeek[];
};

export const PublishScheduleFilters = ({
  eventTypes,
  setEventTypes,
  weekTitles,
  setWeekTitles,
  venueIds,
  setVenueIds,
  priceSchemeIds,
  setPriceSchemeIds,
  weeks,
}: PublishScheduleFiltersProps) => {
  const weekOptions: SelectedMultiOption[] = useMemo(() => {
    const titles = weeks.map((week) => week.title);
    const alwaysShownTitles = [THIS_WEEK_TITLE, NEXT_WEEK_TITLE].filter((title) => !titles.includes(title));

    return [...alwaysShownTitles, ...titles].map((title, index) => ({ id: title, label: title, display_order: index }));
  }, [weeks]);

  const { data: venues = [], isLoading: isVenuesLoading } = useVenues();
  const venueOptions: SelectedMultiOption[] = useMemo(
    () => venues.map((venue, index) => ({ id: venue.venue_id, label: venue.name, display_order: index })),
    [venues],
  );

  const { data: priceSchemes = [], isLoading: isPriceSchemesLoading } = usePriceSchemes();
  const priceSchemeOptions: SelectedMultiOption[] = useMemo(
    () =>
      priceSchemes.map((priceScheme, index) => ({ id: priceScheme.id, label: priceScheme.name, display_order: index })),
    [priceSchemes],
  );

  return (
    <>
      <MultiSelect
        Icon={Export}
        label="Publish type"
        options={PUBLISH_TYPE_OPTIONS}
        selected={eventTypes}
        onChange={setEventTypes}
      />
      <MultiSelect
        Icon={ConfigureIcon}
        label="Date range"
        options={weekOptions}
        selected={weekTitles}
        onChange={setWeekTitles}
      />
      <MultiSelect
        Icon={VenueIcon}
        label="Venue"
        options={venueOptions}
        selected={venueIds}
        onChange={setVenueIds}
        loading={isVenuesLoading}
      />
      <MultiSelect
        Icon={PricingIcon}
        label="Price scheme"
        options={priceSchemeOptions}
        selected={priceSchemeIds}
        onChange={setPriceSchemeIds}
        loading={isPriceSchemesLoading}
      />
    </>
  );
};

export default PublishScheduleFilters;
